import { T, useGT } from 'gt-react';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  TrashIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import type { ApExceptionOwnerType } from '@erp/shared-types';
import { AP_EXCEPTION_OWNER_TYPES } from '@erp/shared-types';
import { Button } from 'src/components/ui/button';
import { Label } from 'src/components/ui/label';
import { Textarea } from 'src/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from 'src/components/ui/toggle-group';
import {
  SelectDropdown,
  type SelectDropdownOption,
} from 'src/components/common/SelectDropdown';
import {
  TaskTypeAgentPicker,
  TaskTypeAllocationPicker,
  TaskTypeUserGroupPicker,
  TaskTypeUserPicker,
} from 'src/pages/authenticated/settings/organization/components/cards/task-types/TaskTypeDefaultAssigneeField';
import { ExceptionTaskTypePicker } from './ExceptionTaskTypePicker';
import { WorkflowPicker } from './WorkflowPicker';
import type { RuleDraft } from './exceptionRuleSetDraft';

export interface ExceptionTypeOption {
  id: string;
  name: string;
  description: string | null;
}

function isOwnerType(value: string): value is ApExceptionOwnerType {
  const kinds: readonly string[] = AP_EXCEPTION_OWNER_TYPES;
  return kinds.includes(value);
}

/**
 * One rule in the ordered list: exception type, optional LLM guidance, the
 * owner, and optionally a task type opened per exception. Without a task
 * type, the owner receives the wired hand-off task when this type fires first.
 */
export function ExceptionRuleEditorRow({
  index,
  total,
  rule,
  typeOptions,
  isStale,
  takenTaskTypeKeys,
  disabled,
  onChange,
  onMove,
  onRemove,
}: {
  index: number;
  total: number;
  rule: RuleDraft;
  /** Exception types this rule may pick (unused ones + its own). */
  typeOptions: ExceptionTypeOption[];
  /** True when the rule's type is no longer an option of the enum. */
  isStale: boolean;
  /** Task type keys picked by the other rules. */
  takenTaskTypeKeys: ReadonlySet<string>;
  disabled?: boolean;
  onChange: (next: RuleDraft) => void;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
}) {
  const gt = useGT();
  const options: SelectDropdownOption[] = typeOptions.map((option) => ({
    id: option.id,
    label: option.description
      ? `${option.name} — ${option.description}`
      : option.name,
    display: option.name,
  }));
  const selectedType =
    options.find((option) => option.id === rule.exceptionTypeOptionId) ?? null;
  const typeFieldId = `exception-rule-type-${rule.key}`;
  const guidanceFieldId = `exception-rule-guidance-${rule.key}`;
  const ownerLabelId = `exception-rule-owner-${rule.key}`;
  const taskTypeFieldId = `exception-rule-task-type-${rule.key}`;

  return (
    <div className="min-w-0 space-y-3 rounded-md border p-3">
      <div className="flex min-w-0 items-start gap-2">
        <span className="mt-1.5 w-6 shrink-0 text-xs font-medium text-muted-foreground">
          {index + 1}.
        </span>
        <div className="min-w-0 flex-1 space-y-1">
          <Label htmlFor={typeFieldId} className="text-xs">
            <T context="exception rule editor: field label for the exception type">
              Exception type
            </T>
          </Label>
          <SelectDropdown
            id={typeFieldId}
            options={options}
            value={selectedType}
            onChange={(option) =>
              onChange({ ...rule, exceptionTypeOptionId: option?.id ?? '' })
            }
            placeholder={gt('Select an exception type…', {
              context: 'exception rule editor: type picker placeholder',
            })}
            searchPlaceholder={gt('Search types…', {
              context: 'exception rule editor: type picker search',
            })}
            size="sm"
            fullWidth
            disabled={disabled}
          />
          {isStale && (
            <p className="flex items-center gap-1 text-xs text-destructive">
              <WarningCircleIcon className="h-3.5 w-3.5" />
              <T context="exception rule editor: warning that the rule's type is no longer in the enum">
                This type is no longer an option of the selected enum. Pick
                another type or remove the rule.
              </T>
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled || index === 0}
            onClick={() => onMove(-1)}
            aria-label={gt('Move rule up', {
              context: 'exception rule editor: reorder button',
            })}
          >
            <ArrowUpIcon className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled || index === total - 1}
            onClick={() => onMove(1)}
            aria-label={gt('Move rule down', {
              context: 'exception rule editor: reorder button',
            })}
          >
            <ArrowDownIcon className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled}
            onClick={onRemove}
            aria-label={gt('Remove rule', {
              context: 'exception rule editor: remove button',
            })}
          >
            <TrashIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="space-y-1 pl-8">
        <Label htmlFor={guidanceFieldId} className="text-xs">
          <T context="exception rule editor: field label for optional LLM detection guidance">
            Detection guidance (optional)
          </T>
        </Label>
        <Textarea
          id={guidanceFieldId}
          value={rule.detectionGuidance}
          disabled={disabled}
          rows={2}
          maxLength={2000}
          placeholder={gt(
            'Extra instruction for the evaluator, e.g. "Raise only when the freight line differs from the carrier confirmation by more than 5%."',
            { context: 'exception rule editor: guidance placeholder' },
          )}
          onChange={(event) =>
            onChange({ ...rule, detectionGuidance: event.target.value })
          }
        />
      </div>

      <div className="space-y-2 pl-8">
        <Label id={ownerLabelId} className="text-xs">
          <T context="exception rule editor: field label for who owns the invoice when this type fires">
            Owner
          </T>
        </Label>
        <ToggleGroup
          type="single"
          aria-labelledby={ownerLabelId}
          value={rule.ownerType}
          onValueChange={(value) => {
            if (isOwnerType(value)) {
              onChange({ ...rule, ownerType: value, ownerId: '' });
            }
          }}
          variant="outline"
          size="sm"
          disabled={disabled}
          className="w-fit max-w-full flex-wrap"
        >
          <ToggleGroupItem value="user" className="flex-none px-2.5">
            <T context="exception rule owner kind toggle, a person">User</T>
          </ToggleGroupItem>
          <ToggleGroupItem value="user_group" className="flex-none px-2.5">
            <T context="exception rule owner kind toggle, a user group">Team</T>
          </ToggleGroupItem>
          <ToggleGroupItem value="agent" className="flex-none px-2.5">
            <T context="exception rule owner kind toggle, an AI agent">Agent</T>
          </ToggleGroupItem>
          <ToggleGroupItem value="workflow" className="flex-none px-2.5">
            <T context="exception rule owner kind toggle, a workflow to trigger">
              Workflow
            </T>
          </ToggleGroupItem>
        </ToggleGroup>

        {rule.ownerType === 'user' && (
          <TaskTypeUserPicker
            value={rule.ownerId}
            disabled={disabled}
            onChange={(ownerId) => onChange({ ...rule, ownerId })}
          />
        )}
        {rule.ownerType === 'agent' && (
          <TaskTypeAgentPicker
            value={rule.ownerId}
            disabled={disabled}
            onChange={(ownerId) => onChange({ ...rule, ownerId })}
          />
        )}
        {rule.ownerType === 'workflow' && (
          <WorkflowPicker
            value={rule.ownerId}
            disabled={disabled}
            onChange={(ownerId) => onChange({ ...rule, ownerId })}
          />
        )}
        {rule.ownerType === 'user_group' && (
          <>
            <TaskTypeUserGroupPicker
              value={rule.ownerId}
              disabled={disabled}
              onChange={(ownerId) => onChange({ ...rule, ownerId })}
            />
            <TaskTypeAllocationPicker
              value={rule.allocation}
              disabled={disabled}
              onChange={(allocation) => onChange({ ...rule, allocation })}
            />
          </>
        )}
      </div>

      <div className="space-y-1 pl-8">
        <Label htmlFor={taskTypeFieldId} className="text-xs">
          <T context="exception rule editor: field label for the task type opened per exception">
            Task type
          </T>
        </Label>
        <ExceptionTaskTypePicker
          id={taskTypeFieldId}
          value={rule.taskTypeKey}
          takenKeys={takenTaskTypeKeys}
          disabled={disabled}
          onChange={(taskTypeKey) => onChange({ ...rule, taskTypeKey })}
        />
        <p className="text-xs text-muted-foreground">
          <T context="exception rule editor: help text for the task type field">
            Opens a task of this type on the invoice while the exception is
            open, and closes it when re-evaluation resolves the exception.
          </T>
        </p>
      </div>
    </div>
  );
}
